/* eslint-disable react/require-default-props */
import React, { FC, useState, useEffect } from 'react'
import { DialogContent, DialogTitle, styled } from '@mui/material'
import { Chip, colors, typography, Dialog } from '@resiliatech/birdsong'
import { useAppState } from 'Context/AppContext/AppContext'
import { AI } from 'Components/Pages/DonationPortal/OptimizationModal/svg'
import { stories } from 'Modules/Analytics/trackActionInterface/Impact/Stories'
import { StoryAiExplanation } from './StoryAiExplanation'
import { StoryAiLoader } from './StoryAiLoader'
import { StoryAiError } from './StoryAiError'
import useAiStorySubmission from './StoryAiHook'
import {
  AiGoalsStoryInput,
  AiStoryInput,
  StoryVariants,
  VariantSpecificData,
} from './StoryAiTypes'
import { StoryAiInputContainer } from './StoryAiInputs/StoryAiInputContainer'

type Props = {
  handleClose: (val: boolean) => void
  open: boolean
  storyEntryPoint?: StoryVariants
  variantSpecificData?: VariantSpecificData
}

const STORY_VARIANT_HEIGHTS = {
  [StoryVariants.STORY]: 719,
  [StoryVariants.GOALS]: 530,
}

const STORY_VARIANT_LOADER_HEIGHTS = {
  [StoryVariants.STORY]: 408,
  [StoryVariants.GOALS]: 440,
}

export const StoryAiDialog: FC<Props> = ({
  handleClose,
  open,
  storyEntryPoint = StoryVariants.STORY,
  variantSpecificData,
}) => {
  const {
    nonprofitOrgConfig: { orgId, platformPlan, role, nonprofitOrgRole },
    funderOrgConfig: { orgId: funderOrgId },
    userConfig: { id, email },
  } = useAppState()

  const [storyVariant, setStoryVariant] =
    useState<StoryVariants>(storyEntryPoint)

  const [aiInput, setAiInput] = useState<AiStoryInput | AiGoalsStoryInput>({
    purpose: undefined,
    audience: undefined,
    impact: '',
    importance: '',
    additionalRequests: '',
    goal: variantSpecificData?.goal?.preSelectedGoal?.id || undefined,
  })

  useEffect(() => {
    if (variantSpecificData?.goal?.preSelectedGoal?.id) {
      const goal = variantSpecificData.goal.preSelectedGoal.id
      setAiInput(() => ({
        ...aiInput,
        goal,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantSpecificData?.goal?.preSelectedGoal?.id])

  const [focusedInput, setFocusedInput] = useState<string>(
    storyEntryPoint === StoryVariants.STORY ? 'initial' : 'goal'
  )
  const { handleSubmit, loading, hasError, completed } = useAiStorySubmission()

  const segmentMap = {
    purpose: stories.Ai_Purpose_Selected,
    audience: stories.Ai_Audience_Selected,
    impact: stories.Ai_Impact_Selected,
    importance: stories.Ai_Importance_Selected,
    additionalRequests: stories.Ai_Additionalrequests_Selected,
    goal: stories.Ai_Goal_Selected,
  }

  const handleChange = (value: any, key: string) => {
    let goalId = null
    if (storyVariant === StoryVariants.GOALS && 'goal' in aiInput) {
      goalId = aiInput.goal
    }
    if (key === 'purpose' || key === 'audience' || key === 'goal') {
      const segmentEvent = segmentMap[key]
      if (segmentEvent) {
        const segmentProps = {
          userId: id,
          email,
          funderOrgId,
          role,
          goalId,
          relationship: nonprofitOrgRole || '',
          nonprofitOrgId: orgId,
          platformPlan,
          ...(key === 'purpose' && { purposeSelected: value }),
          ...(key === 'audience' && { audienceSelected: value }),
          ...(key === 'goal' && { goalId: value }),
          storyVariant,
        }
        segmentEvent(segmentProps)
      }
    }
    setAiInput({
      ...aiInput,
      [key]: value,
    })
  }

  const handleFocusChange = (input: string) => {
    if (
      input === 'impact' ||
      input === 'importance' ||
      input === 'additionalRequests'
    ) {
      const segmentEvent = segmentMap[input]
      if (segmentEvent) {
        segmentEvent({
          userId: id,
          email,
          funderOrgId,
          role,
          relationship: nonprofitOrgRole || '',
          nonprofitOrgId: orgId,
          platformPlan,
          storyVariant,
        })
      }
    }

    setFocusedInput(input)
  }

  /*
   * Handles the "use goals progress" checkbox change
   * @note may want to extract this to a hook or util function
   * in the future if the variant logic gets complex
   */
  const handleCheckboxChange = (checked: boolean) => {
    const newVariant = checked ? StoryVariants.GOALS : StoryVariants.STORY
    const newFocus = checked ? 'goal' : 'initial'

    // Set new states
    setStoryVariant(newVariant)
    setFocusedInput(newFocus)
  }

  useEffect(() => {
    if (storyVariant !== undefined) {
      // Make sure storyVariant is defined to avoid running on initial render
      stories.Ai_Goal_Progress_Clicked({
        userId: id,
        nonprofitOrgId: orgId,
        platformPlan,
        funderOrgId,
        email,
        role,
        relationship: nonprofitOrgRole || '',
        storyVariant,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyVariant])

  return (
    <StyledDialog
      open={open}
      maxWidth='md'
      onClose={() => {
        setFocusedInput(
          storyEntryPoint === StoryVariants.STORY ? 'initial' : 'goal'
        )
        handleClose(false)
      }}
      className={loading || completed ? 'loading' : ''}
      hasError={hasError}
      variantHeight={STORY_VARIANT_HEIGHTS[storyVariant]}
      variantLoaderHeight={STORY_VARIANT_LOADER_HEIGHTS[storyVariant]}
      disableEscapeKeyDown={loading || completed}
    >
      <Title>
        {loading || completed
          ? 'Drafting story with'
          : 'Draft your story with '}
        <span>
          Resilia AI <AI />
        </span>
        <Chip text='Beta ' backgroundColor='rgba(0, 0, 0, 0.08)' />
      </Title>

      <StoryAiError hasError={hasError} />
      <StyledContent loading={loading || completed}>
        {/* THIS OPEN CHECK IS SO THE GOALS QUERY DOESN'T FIRE UNTIL NEEDED */}
        {open && (
          <StoryAiInputContainer
            aiInput={aiInput as AiStoryInput}
            handleChange={handleChange}
            handleFocusChange={handleFocusChange}
            handleCheckboxChange={handleCheckboxChange}
            handleSubmit={() => handleSubmit({ aiInput, storyVariant })}
            storyVariant={storyVariant}
            variantSpecificData={variantSpecificData}
          />
        )}
        <StoryAiExplanation focusedInput={focusedInput} />
      </StyledContent>
      <StoryAiLoader
        loading={loading}
        completed={completed}
        storyVariant={storyVariant}
      />
    </StyledDialog>
  )
}

const StyledDialog = styled(Dialog, {
  shouldForwardProp: (prop) =>
    prop !== 'variantHeight' &&
    prop !== 'variantLoaderHeight' &&
    prop !== 'hasError',
})<{
  hasError: boolean
  variantHeight: number
  variantLoaderHeight: number
}>`
  & .MuiDialog-paper {
    background-color: ${colors.background.tertiaryBackground};
    width: 840px;
    /* error height is 81px */
    height: ${({ hasError, variantHeight }) =>
      hasError ? `${variantHeight + 81}px` : `${variantHeight}px`};
    transition: height 0.5s ease-in-out;
  }

  &.loading {
    button {
      display: none;
      pointer-events: none;
    }
    & .MuiDialog-paper {
      background-color: ${colors.background.tertiaryBackground};
      width: 600px;
      height: ${({ variantLoaderHeight }) => `${variantLoaderHeight}px`};
      transition: all 0.5s ease-in-out;
      overflow: hidden;
    }
  }
`

const StyledContent = styled(DialogContent, {
  shouldForwardProp: (prop) => prop !== 'loading',
})<{ loading?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  opacity: ${({ loading }) => (loading ? 0 : 1)};
  transition: opacity 0.5s ease-in-out;
  overflow-y: auto;
`

const Title = styled(DialogTitle)`
  ${typography.up2.semiBoldSans.styles}
  padding: 24px;
  display: flex;
  align-items: center;
  line-height: 135%;
  font-size: 24px !important;
  height: 84px;

  span {
    color: #4d3d99;
    display: inline-flex;
    align-items: center;
    margin: 0 12px 0 4px;
    svg {
      margin-left: 4px;
    }
  }
  div {
    line-height: 135%;
  }
`
